/**
 * @module useAircraft
 */
import { useCallback } from 'react'
import {
  useGetAircraftMutation,
  useGetAircraftByIdMutation,
  useGetAircraftByRegistrationMutation,
  useCreateAircraftMutation,
  useUpdateAircraftMutation,
  useDeleteAircraftMutation,
  useUpdateAircraftStatusMutation,
  useUpdateSpecificationsMutation,
  useUpdateConfigurationMutation,
  useUpdateMaintenanceMutation,
  useUpdateOperationalInfoMutation,
  useUpdateAirworthinessMutation,
  useUpdateInsuranceMutation,
  useAddModificationMutation,
  useRemoveModificationMutation,
  useUpdateMaintenanceStatusMutation,
  useAddMaintenanceRecordMutation,
  useUpdateAirworthinessDirectiveMutation,
  useGetActiveAircraftMutation,
  useGetAircraftNeedingMaintenanceMutation,
  useGetAircraftByAirlineMutation,
  useGetAircraftByTypeMutation,
  useGetAircraftByLocationMutation,
  useGetAircraftWithExpiringAirworthinessMutation,
  useGetAircraftWithExpiringInsuranceMutation,
} from '../services/aircraft.service'

export const useAircraft = () => {
  // Basic CRUD operations
  const [executeGetAircraft, { isLoading: isGetAircraftLoading }] = useGetAircraftMutation()
  const [executeGetAircraftById, { isLoading: isGetAircraftByIdLoading }] =
    useGetAircraftByIdMutation()
  const [executeGetAircraftByRegistration, { isLoading: isGetAircraftByRegistrationLoading }] =
    useGetAircraftByRegistrationMutation()
  const [executeCreateAircraft, { isLoading: isCreateAircraftLoading }] =
    useCreateAircraftMutation()
  const [executeUpdateAircraft, { isLoading: isUpdateAircraftLoading }] =
    useUpdateAircraftMutation()
  const [executeDeleteAircraft, { isLoading: isDeleteAircraftLoading }] =
    useDeleteAircraftMutation()

  // Status and profile updates
  const [executeUpdateAircraftStatus, { isLoading: isUpdateAircraftStatusLoading }] =
    useUpdateAircraftStatusMutation()
  const [executeUpdateSpecifications, { isLoading: isUpdateSpecificationsLoading }] =
    useUpdateSpecificationsMutation()
  const [executeUpdateConfiguration, { isLoading: isUpdateConfigurationLoading }] =
    useUpdateConfigurationMutation()
  const [executeUpdateMaintenance, { isLoading: isUpdateMaintenanceLoading }] =
    useUpdateMaintenanceMutation()
  const [executeUpdateOperationalInfo, { isLoading: isUpdateOperationalInfoLoading }] =
    useUpdateOperationalInfoMutation()
  const [executeUpdateAirworthiness, { isLoading: isUpdateAirworthinessLoading }] =
    useUpdateAirworthinessMutation()
  const [executeUpdateInsurance, { isLoading: isUpdateInsuranceLoading }] =
    useUpdateInsuranceMutation()

  // Modification operations
  const [executeAddModification, { isLoading: isAddModificationLoading }] =
    useAddModificationMutation()
  const [executeRemoveModification, { isLoading: isRemoveModificationLoading }] =
    useRemoveModificationMutation()

  // Maintenance operations
  const [executeUpdateMaintenanceStatus, { isLoading: isUpdateMaintenanceStatusLoading }] =
    useUpdateMaintenanceStatusMutation()
  const [executeAddMaintenanceRecord, { isLoading: isAddMaintenanceRecordLoading }] =
    useAddMaintenanceRecordMutation()
  const [
    executeUpdateAirworthinessDirective,
    { isLoading: isUpdateAirworthinessDirectiveLoading },
  ] = useUpdateAirworthinessDirectiveMutation()

  // Query operations
  const [executeGetActiveAircraft, { isLoading: isGetActiveAircraftLoading }] =
    useGetActiveAircraftMutation()
  const [
    executeGetAircraftNeedingMaintenance,
    { isLoading: isGetAircraftNeedingMaintenanceLoading },
  ] = useGetAircraftNeedingMaintenanceMutation()
  const [executeGetAircraftByAirline, { isLoading: isGetAircraftByAirlineLoading }] =
    useGetAircraftByAirlineMutation()
  const [executeGetAircraftByType, { isLoading: isGetAircraftByTypeLoading }] =
    useGetAircraftByTypeMutation()
  const [executeGetAircraftByLocation, { isLoading: isGetAircraftByLocationLoading }] =
    useGetAircraftByLocationMutation()

  // Compliance and monitoring
  const [
    executeGetAircraftWithExpiringAirworthiness,
    { isLoading: isGetAircraftWithExpiringAirworthinessLoading },
  ] = useGetAircraftWithExpiringAirworthinessMutation()
  const [
    executeGetAircraftWithExpiringInsurance,
    { isLoading: isGetAircraftWithExpiringInsuranceLoading },
  ] = useGetAircraftWithExpiringInsuranceMutation()

  // Basic CRUD async functions
  const getAircraftAsync = useCallback(async () => {
    if (isGetAircraftLoading) {
      return
    }
    try {
      const response = await executeGetAircraft().unwrap()
      return response.data.aircraft
    } catch (error) {
      console.error('#getAircraftAsync: Error fetching aircraft', error)
      throw error
    }
  }, [executeGetAircraft, isGetAircraftLoading])

  const getAircraftByIdAsync = useCallback(
    async (aircraftId) => {
      if (isGetAircraftByIdLoading) {
        return
      }
      try {
        const response = await executeGetAircraftById(aircraftId).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#getAircraftByIdAsync: Error fetching aircraft by ID', error)
        throw error
      }
    },
    [executeGetAircraftById, isGetAircraftByIdLoading],
  )

  const getAircraftByRegistrationAsync = useCallback(
    async (registrationNumber) => {
      if (isGetAircraftByRegistrationLoading) {
        return
      }
      try {
        const response = await executeGetAircraftByRegistration(registrationNumber).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error(
          '#getAircraftByRegistrationAsync: Error fetching aircraft by registration',
          error,
        )
        throw error
      }
    },
    [executeGetAircraftByRegistration, isGetAircraftByRegistrationLoading],
  )

  const createAircraftAsync = useCallback(
    async (aircraft) => {
      if (isCreateAircraftLoading) {
        return
      }
      try {
        const response = await executeCreateAircraft(aircraft).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#createAircraftAsync: Error creating aircraft', error)
        throw error
      }
    },
    [executeCreateAircraft, isCreateAircraftLoading],
  )

  const updateAircraftAsync = useCallback(
    async (aircraftId, updates) => {
      if (isUpdateAircraftLoading) {
        return
      }
      try {
        const response = await executeUpdateAircraft({ aircraftId, ...updates }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateAircraftAsync: Error updating aircraft', error)
        throw error
      }
    },
    [executeUpdateAircraft, isUpdateAircraftLoading],
  )

  const deleteAircraftAsync = useCallback(
    async (aircraftId) => {
      if (isDeleteAircraftLoading) {
        return
      }
      try {
        const response = await executeDeleteAircraft(aircraftId).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteAircraftAsync: Error deleting aircraft', error)
        throw error
      }
    },
    [executeDeleteAircraft, isDeleteAircraftLoading],
  )

  // Status and profile async functions
  const updateAircraftStatusAsync = useCallback(
    async (aircraftId, status) => {
      if (isUpdateAircraftStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateAircraftStatus({ aircraftId, status }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateAircraftStatusAsync: Error updating aircraft status', error)
        throw error
      }
    },
    [executeUpdateAircraftStatus, isUpdateAircraftStatusLoading],
  )

  const updateSpecificationsAsync = useCallback(
    async (aircraftId, specifications) => {
      if (isUpdateSpecificationsLoading) {
        return
      }
      try {
        const response = await executeUpdateSpecifications({ aircraftId, specifications }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateSpecificationsAsync: Error updating specifications', error)
        throw error
      }
    },
    [executeUpdateSpecifications, isUpdateSpecificationsLoading],
  )

  const updateConfigurationAsync = useCallback(
    async (aircraftId, configuration) => {
      if (isUpdateConfigurationLoading) {
        return
      }
      try {
        const response = await executeUpdateConfiguration({ aircraftId, configuration }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateConfigurationAsync: Error updating configuration', error)
        throw error
      }
    },
    [executeUpdateConfiguration, isUpdateConfigurationLoading],
  )

  const updateMaintenanceAsync = useCallback(
    async (aircraftId, maintenance) => {
      if (isUpdateMaintenanceLoading) {
        return
      }
      try {
        const response = await executeUpdateMaintenance({ aircraftId, maintenance }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateMaintenanceAsync: Error updating maintenance', error)
        throw error
      }
    },
    [executeUpdateMaintenance, isUpdateMaintenanceLoading],
  )

  const updateOperationalInfoAsync = useCallback(
    async (aircraftId, operationalInfo) => {
      if (isUpdateOperationalInfoLoading) {
        return
      }
      try {
        const response = await executeUpdateOperationalInfo({
          aircraftId,
          operationalInfo,
        }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateOperationalInfoAsync: Error updating operational info', error)
        throw error
      }
    },
    [executeUpdateOperationalInfo, isUpdateOperationalInfoLoading],
  )

  const updateAirworthinessAsync = useCallback(
    async (aircraftId, airworthiness) => {
      if (isUpdateAirworthinessLoading) {
        return
      }
      try {
        const response = await executeUpdateAirworthiness({ aircraftId, airworthiness }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateAirworthinessAsync: Error updating airworthiness', error)
        throw error
      }
    },
    [executeUpdateAirworthiness, isUpdateAirworthinessLoading],
  )

  const updateInsuranceAsync = useCallback(
    async (aircraftId, insurance) => {
      if (isUpdateInsuranceLoading) {
        return
      }
      try {
        const response = await executeUpdateInsurance({ aircraftId, insurance }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateInsuranceAsync: Error updating insurance', error)
        throw error
      }
    },
    [executeUpdateInsurance, isUpdateInsuranceLoading],
  )

  // Modification async functions
  const addModificationAsync = useCallback(
    async (aircraftId, modification) => {
      if (isAddModificationLoading) {
        return
      }
      try {
        const response = await executeAddModification({ aircraftId, modification }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#addModificationAsync: Error adding modification', error)
        throw error
      }
    },
    [executeAddModification, isAddModificationLoading],
  )

  const removeModificationAsync = useCallback(
    async (aircraftId, modification) => {
      if (isRemoveModificationLoading) {
        return
      }
      try {
        const response = await executeRemoveModification({ aircraftId, modification }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#removeModificationAsync: Error removing modification', error)
        throw error
      }
    },
    [executeRemoveModification, isRemoveModificationLoading],
  )

  // Maintenance async functions
  const updateMaintenanceStatusAsync = useCallback(
    async (aircraftId, maintenanceStatus) => {
      if (isUpdateMaintenanceStatusLoading) {
        return
      }
      try {
        const response = await executeUpdateMaintenanceStatus({
          aircraftId,
          maintenanceStatus,
        }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#updateMaintenanceStatusAsync: Error updating maintenance status', error)
        throw error
      }
    },
    [executeUpdateMaintenanceStatus, isUpdateMaintenanceStatusLoading],
  )

  const addMaintenanceRecordAsync = useCallback(
    async (aircraftId, maintenanceRecord) => {
      if (isAddMaintenanceRecordLoading) {
        return
      }
      try {
        const response = await executeAddMaintenanceRecord({
          aircraftId,
          maintenanceRecord,
        }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#addMaintenanceRecordAsync: Error adding maintenance record', error)
        throw error
      }
    },
    [executeAddMaintenanceRecord, isAddMaintenanceRecordLoading],
  )

  const updateAirworthinessDirectiveAsync = useCallback(
    async (aircraftId, airworthinessDirective) => {
      if (isUpdateAirworthinessDirectiveLoading) {
        return
      }
      try {
        const response = await executeUpdateAirworthinessDirective({
          aircraftId,
          airworthinessDirective,
        }).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error(
          '#updateAirworthinessDirectiveAsync: Error updating airworthiness directive',
          error,
        )
        throw error
      }
    },
    [executeUpdateAirworthinessDirective, isUpdateAirworthinessDirectiveLoading],
  )

  // Query async functions
  const getActiveAircraftAsync = useCallback(async () => {
    if (isGetActiveAircraftLoading) {
      return
    }
    try {
      const response = await executeGetActiveAircraft().unwrap()
      return response.data.aircraft
    } catch (error) {
      console.error('#getActiveAircraftAsync: Error fetching active aircraft', error)
      throw error
    }
  }, [executeGetActiveAircraft, isGetActiveAircraftLoading])

  const getAircraftNeedingMaintenanceAsync = useCallback(async () => {
    if (isGetAircraftNeedingMaintenanceLoading) {
      return
    }
    try {
      const response = await executeGetAircraftNeedingMaintenance().unwrap()
      return response.data.aircraft
    } catch (error) {
      console.error(
        '#getAircraftNeedingMaintenanceAsync: Error fetching aircraft needing maintenance',
        error,
      )
      throw error
    }
  }, [executeGetAircraftNeedingMaintenance, isGetAircraftNeedingMaintenanceLoading])

  const getAircraftByAirlineAsync = useCallback(
    async (airlineId) => {
      if (isGetAircraftByAirlineLoading) {
        return
      }
      try {
        const response = await executeGetAircraftByAirline(airlineId).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#getAircraftByAirlineAsync: Error fetching aircraft by airline', error)
        throw error
      }
    },
    [executeGetAircraftByAirline, isGetAircraftByAirlineLoading],
  )

  const getAircraftByTypeAsync = useCallback(
    async (type) => {
      if (isGetAircraftByTypeLoading) {
        return
      }
      try {
        const response = await executeGetAircraftByType(type).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#getAircraftByTypeAsync: Error fetching aircraft by type', error)
        throw error
      }
    },
    [executeGetAircraftByType, isGetAircraftByTypeLoading],
  )

  const getAircraftByLocationAsync = useCallback(
    async (location) => {
      if (isGetAircraftByLocationLoading) {
        return
      }
      try {
        const response = await executeGetAircraftByLocation(location).unwrap()
        return response.data.aircraft
      } catch (error) {
        console.error('#getAircraftByLocationAsync: Error fetching aircraft by location', error)
        throw error
      }
    },
    [executeGetAircraftByLocation, isGetAircraftByLocationLoading],
  )

  // Compliance and monitoring async functions
  const getAircraftWithExpiringAirworthinessAsync = useCallback(async () => {
    if (isGetAircraftWithExpiringAirworthinessLoading) {
      return
    }
    try {
      const response = await executeGetAircraftWithExpiringAirworthiness().unwrap()
      return response.data.aircraft
    } catch (error) {
      console.error(
        '#getAircraftWithExpiringAirworthinessAsync: Error fetching aircraft with expiring airworthiness',
        error,
      )
      throw error
    }
  }, [executeGetAircraftWithExpiringAirworthiness, isGetAircraftWithExpiringAirworthinessLoading])

  const getAircraftWithExpiringInsuranceAsync = useCallback(async () => {
    if (isGetAircraftWithExpiringInsuranceLoading) {
      return
    }
    try {
      const response = await executeGetAircraftWithExpiringInsurance().unwrap()
      return response.data.aircraft
    } catch (error) {
      console.error(
        '#getAircraftWithExpiringInsuranceAsync: Error fetching aircraft with expiring insurance',
        error,
      )
      throw error
    }
  }, [executeGetAircraftWithExpiringInsurance, isGetAircraftWithExpiringInsuranceLoading])

  return {
    // Basic CRUD functions
    getAircraftAsync,
    getAircraftByIdAsync,
    getAircraftByRegistrationAsync,
    createAircraftAsync,
    updateAircraftAsync,
    deleteAircraftAsync,

    // Status and profile functions
    updateAircraftStatusAsync,
    updateSpecificationsAsync,
    updateConfigurationAsync,
    updateMaintenanceAsync,
    updateOperationalInfoAsync,
    updateAirworthinessAsync,
    updateInsuranceAsync,

    // Modification functions
    addModificationAsync,
    removeModificationAsync,

    // Maintenance functions
    updateMaintenanceStatusAsync,
    addMaintenanceRecordAsync,
    updateAirworthinessDirectiveAsync,

    // Query functions
    getActiveAircraftAsync,
    getAircraftNeedingMaintenanceAsync,
    getAircraftByAirlineAsync,
    getAircraftByTypeAsync,
    getAircraftByLocationAsync,

    // Compliance and monitoring functions
    getAircraftWithExpiringAirworthinessAsync,
    getAircraftWithExpiringInsuranceAsync,

    // Loading states
    isGetAircraftLoading,
    isGetAircraftByIdLoading,
    isGetAircraftByRegistrationLoading,
    isCreateAircraftLoading,
    isUpdateAircraftLoading,
    isDeleteAircraftLoading,
    isUpdateAircraftStatusLoading,
    isUpdateSpecificationsLoading,
    isUpdateConfigurationLoading,
    isUpdateMaintenanceLoading,
    isUpdateOperationalInfoLoading,
    isUpdateAirworthinessLoading,
    isUpdateInsuranceLoading,
    isAddModificationLoading,
    isRemoveModificationLoading,
    isUpdateMaintenanceStatusLoading,
    isAddMaintenanceRecordLoading,
    isUpdateAirworthinessDirectiveLoading,
    isGetActiveAircraftLoading,
    isGetAircraftNeedingMaintenanceLoading,
    isGetAircraftByAirlineLoading,
    isGetAircraftByTypeLoading,
    isGetAircraftByLocationLoading,
    isGetAircraftWithExpiringAirworthinessLoading,
    isGetAircraftWithExpiringInsuranceLoading,
  }
}
